import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, FileText, CheckCircle, Clock, ClipboardList } from 'lucide-react';

const ProceduresReclamation = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-slate-500 hover:text-[#8DC63F] transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour à l'accueil
          </Link>
          <h1 className="text-3xl font-bold text-[#0F172A]">Procédures de réclamation</h1>
        </div>

        {/* Infos organisme */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#8DC63F]/10 rounded-lg">
              <FileText className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#0F172A] mb-3">Informations sur l'organisme</h2>
              <div className="text-slate-600 space-y-2">
                <p>
                  <strong>FISL</strong> est un organisme de formation, Identifiant SIRET <strong>51928546400029</strong>, 
                  déclaration d'activité enregistrée sous le numéro <strong>54 17 01377 17</strong> auprès du préfet 
                  de région de Nouvelle Aquitaine, sous la responsabilité de <strong>Madame Triqueneaux Carole</strong>.
                </p>
                <p className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Organisme certifié Qualiopi N° 2020/86192.2 pour les actions de formations
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Définition réclamation */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-blue-800 mb-3">Qu'est-ce qu'une réclamation ?</h2>
          <p className="text-blue-700">
            Une réclamation est une déclaration d'un client / formateur ayant été en contact avec notre organisme 
            de formation et actant de son mécontentement à l'égard de ce dernier.
          </p>
        </div>

        {/* Comment adresser une réclamation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-4">Comment adresser une réclamation ?</h2>
          <p className="text-slate-600 mb-4">Les réclamations doivent être adressées par courrier, par email, ou téléphone :</p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Par email */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#8DC63F]/10 rounded-lg">
                  <Mail className="h-5 w-5 text-[#8DC63F]" />
                </div>
                <h3 className="font-semibold text-[#0F172A]">Par email</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Un courriel à l'adresse suivante : <br />
                <a href="mailto:direction@fisl.fr" className="text-[#8DC63F] font-medium hover:underline">
                  direction@fisl.fr
                </a>
              </p>
              <p className="text-slate-500 text-sm mt-2">
                En précisant <strong>"Réclamation"</strong> dans l'objet du courriel et le scan de l'imprimé à remplir.
              </p>
            </div>

            {/* Par téléphone */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#8DC63F]/10 rounded-lg">
                  <Phone className="h-5 w-5 text-[#8DC63F]" />
                </div>
                <h3 className="font-semibold text-[#0F172A]">Par téléphone</h3>
              </div>
              <p className="text-slate-600 text-sm">
                Un appel téléphonique au numéro suivant : <br />
                <a href="tel:0637265884" className="text-[#8DC63F] font-medium hover:underline">
                  06 37 26 58 84
                </a>
              </p>
              <p className="text-slate-500 text-sm mt-2">
                Une confirmation écrite ou par email sera systématiquement demandée.
              </p>
            </div>
          </div>
        </div>

        {/* Traitement de la réclamation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-4">Traitement de votre réclamation</h2>
          <p className="text-slate-600 mb-6">
            À la réception de la réclamation, <strong>Madame Triqueneaux Carole</strong>, responsable de l'organisme FISL, 
            procède aux opérations suivantes :
          </p>

          {/* Étape 1 */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#8DC63F] text-white font-bold text-sm">
                1
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">Enregistrement de la réclamation</h3>
            </div>
            <div className="ml-11 bg-slate-50 rounded-lg p-4 border border-slate-200">
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-400" />
                  Date de réception
                </li>
                <li className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-400" />
                  Nom du client
                </li>
                <li className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-400" />
                  Formation concernée
                </li>
                <li className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-400" />
                  Description de la réclamation
                </li>
              </ul>
              <p className="mt-3 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                Si la réclamation était jugée incomplète, une demande d'information complémentaire 
                serait adressée au client afin de pouvoir traiter le dossier dans de bonnes conditions.
              </p>
            </div>
          </div>

          {/* Étape 2 */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#8DC63F] text-white font-bold text-sm">
                2
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">Traitement de la réclamation</h3>
            </div>
            <div className="ml-11 bg-slate-50 rounded-lg p-4 border border-slate-200">
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-slate-400" />
                  Actions mises en place
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-slate-400" />
                  Résolution apportée
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-slate-400" />
                  Date de réponse au client
                </li>
              </ul>
              <div className="mt-3 flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <Clock className="h-4 w-4" />
                <span>
                  Le traitement de la réponse et l'envoi de la réponse au client se fait dans un délai de 
                  <strong> 15 jours ouvrables</strong> à compter de la réception de la réclamation.
                </span>
              </div>
            </div>
          </div>

          {/* Étape 3 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#8DC63F] text-white font-bold text-sm">
                3
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A]">Consignation au registre des réclamations</h3>
            </div>
            <div className="ml-11 bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-slate-600 mb-3">Sont consignées au registre des réclamations :</p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                  La réclamation écrite du client (détaillée dans le registre des réclamations)
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                  Les actions et solutions apportées à la réclamation
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                  Une copie de la réponse apportée au client
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact rapide */}
        <div className="bg-gradient-to-r from-[#8DC63F]/10 to-[#8DC63F]/5 rounded-xl border border-[#8DC63F]/20 p-6">
          <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Contact pour les réclamations</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:direction@fisl.fr?subject=Réclamation" 
              className="flex items-center justify-center gap-2 px-6 py-3 bg-[#8DC63F] text-white rounded-lg hover:bg-[#7db336] transition-colors font-medium"
            >
              <Mail className="h-5 w-5" />
              direction@fisl.fr
            </a>
            <a 
              href="tel:0637265884" 
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#0F172A] border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors font-medium"
            >
              <Phone className="h-5 w-5" />
              06 37 26 58 84
            </a>
          </div>
        </div>

        {/* Footer links */}
        <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap gap-4 justify-center text-sm text-slate-500">
          <Link to="/mentions-legales" className="hover:text-[#8DC63F] transition-colors">
            Mentions légales
          </Link>
          <span>•</span>
          <Link to="/rgpd" className="hover:text-[#8DC63F] transition-colors">
            Politique de confidentialité
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProceduresReclamation;
